import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "../axios";
import AwardsDrive from "../components/AwardsDrive";
import EnterYourCar from "../components/EnterYourCar";
import WinnerPhotoCard from "../components/WinnerPhotoCard";
import { showDate, typeName } from "../components/ShowPieces";

const tabs = [
  { id: "awards", label: "Awards", to: "/awards" },
  { id: "entry", label: "Entry Rules", to: "/awards?section=entry" },
  { id: "judging", label: "Judging Rules", to: "/awards?section=judging" },
];

const winnerFilters = [
  { key: "type", label: "Type" },
  { key: "event", label: "Event" },
  { key: "className", label: "Class" },
  { key: "make", label: "Make" },
  { key: "model", label: "Model" },
];

function winnerValue(item, key) {
  if (key === "type") {
    return item.vehicle_type || "";
  }
  if (key === "event") {
    return item.event ? String(item.event) : "";
  }
  if (key === "className") {
    return item.car_class_title || "";
  }
  if (key === "make") {
    return item.vehicle?.manufacture || "";
  }
  if (key === "model") {
    return item.vehicle?.model || "";
  }
  return "";
}

function winnerLabel(item, key) {
  if (key === "type") {
    return typeName(item.vehicle_type);
  }
  if (key === "event") {
    return item.event_title || "Show";
  }
  return winnerValue(item, key);
}

function yearBound(value) {
  return /^\d{4}$/.test(value) ? Number(value) : null;
}

function inYearRange(item, filters) {
  const from = yearBound(filters.yearFrom);
  const to = yearBound(filters.yearTo);
  if (from === null && to === null) {
    return true;
  }
  const year = Number(item.vehicle?.year);
  if (!Number.isFinite(year)) {
    return false;
  }
  if (from !== null && year < from) {
    return false;
  }
  if (to !== null && year > to) {
    return false;
  }
  return true;
}

function filterOptions(winners, filters, key) {
  const pool = winners.filter(
    (item) =>
      inYearRange(item, filters) &&
      winnerFilters.every(
        (filter) =>
          filter.key === key || !filters[filter.key] || winnerValue(item, filter.key) === filters[filter.key]
      )
  );
  const labels = new Map();
  pool.forEach((item) => {
    const value = winnerValue(item, key);
    if (value && !labels.has(value)) {
      labels.set(value, winnerLabel(item, key));
    }
  });
  let rows = [...labels.entries()].map(([value, label]) => ({ value, label }));
  if (key === "event") {
    const titles = {};
    rows.forEach((row) => {
      titles[row.label] = (titles[row.label] || 0) + 1;
    });
    if (Object.values(titles).some((count) => count > 1)) {
      rows = rows.map((row) => {
        const sample = pool.find((item) => winnerValue(item, "event") === row.value);
        const date = sample?.event_date ? showDate(sample.event_date) : "";
        return date ? { ...row, label: `${row.label} · ${date}` } : row;
      });
    }
  }
  rows.sort((a, b) => a.label.localeCompare(b.label));
  return rows;
}

function settledFilters(winners, filters) {
  const next = { ...filters };
  let changed = true;
  while (changed) {
    changed = false;
    winnerFilters.forEach((filter) => {
      if (!next[filter.key]) {
        return;
      }
      const options = filterOptions(winners, next, filter.key);
      if (!options.some((row) => row.value === next[filter.key])) {
        next[filter.key] = "";
        changed = true;
      }
    });
  }
  return next;
}

const banners = {
  awards: {
    kicker: "Awards",
    title: "What the judges called",
    lede: "Class winners from the show.",
  },
  entry: {
    kicker: "Entry rules",
    title: "Rules of entry",
    lede: "Rules for the upcoming shows.",
  },
  judging: {
    kicker: "Judging rules",
    title: "How the cars are judged",
    lede: "Preparation happens before the car arrives. The last of it usually happens on the field.",
  },
};

function AwardWinnerList({ winners, filters, across, onFilter, onAcross }) {
  const shown = winners.filter(
    (item) =>
      inYearRange(item, filters) &&
      winnerFilters.every((filter) => !filters[filter.key] || winnerValue(item, filter.key) === filters[filter.key])
  );

  const field = (filter) => {
    const options = filterOptions(winners, filters, filter.key);
    return (
      <label key={filter.key} className="award-filter">
        <span>{filter.label}</span>
        <select value={filters[filter.key]} onChange={(event) => onFilter(filter.key, event.target.value)}>
          <option value="">All</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  };

  return (
    <>
      <div className="award-filter-rows">
        <div className="award-filter-row">{winnerFilters.slice(0, 3).map(field)}</div>
        <div className="award-filter-row is-rest">
          {field(winnerFilters[3])}
          <div className="award-year">
            <label>
              <span>Year from</span>
              <input
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                value={filters.yearFrom}
                title="Year from"
                onChange={(event) => onFilter("yearFrom", event.target.value.replace(/\D/g, "").slice(0, 4))}
              />
            </label>
            <label>
              <span>Year to</span>
              <input
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                value={filters.yearTo}
                title="Year to"
                onChange={(event) => onFilter("yearTo", event.target.value.replace(/\D/g, "").slice(0, 4))}
              />
            </label>
          </div>
          {field(winnerFilters[4])}
          <div className="award-density" role="group" aria-label="Winners in a row">
            {[3, 5].map((count) => (
              <button
                key={count}
                type="button"
                aria-pressed={across === count}
                title={`${count} across`}
                onClick={() => onAcross(count)}
              >
                {count} across
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="award-filter-rule" />
      {shown.length === 0 ? (
        <p>No class winner matches these filters.</p>
      ) : (
        <div className={`award-winner-grid is-${across}`}>
          {shown.map((item) => (
            <WinnerPhotoCard key={item.id} item={item} compact={across === 5} newTab />
          ))}
        </div>
      )}
    </>
  );
}

function AwardsScreen() {
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("section");
  const section = requested === "entry" || requested === "judging" ? requested : "awards";
  const [winners, setWinners] = useState([]);
  const [filters, setFilters] = useState({
    type: "",
    event: "",
    className: "",
    make: "",
    yearFrom: "",
    yearTo: "",
    model: "",
  });
  const [across, setAcross] = useState(3);
  const [entryEvents, setEntryEvents] = useState(null);
  const entryLede = entryEvents && entryEvents.length
    ? entryEvents.map((item) => showDate(item.date)).join(" · ")
    : banners.entry.lede;
  const banner = section === "entry" ? { ...banners.entry, lede: entryLede } : banners[section];
  const [driving, setDriving] = useState(false);
  const [passer, setPasser] = useState(null);
  const driveTimer = useRef(0);
  const drivingRef = useRef(false);
  const lastPasser = useRef(null);

  function beginDrive(id) {
    if (id === section || drivingRef.current) {
      return;
    }
    const choices = ["tower", "palm", "umbrella", "gull", "board"].filter((item) => item !== lastPasser.current);
    const next = choices[Math.floor(Math.random() * choices.length)];
    lastPasser.current = next;
    drivingRef.current = true;
    setPasser(next);
    setDriving(false);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setDriving(true);
        driveTimer.current = window.setTimeout(() => {
          drivingRef.current = false;
          setDriving(false);
        }, 1700);
      });
    });
  }

  useEffect(() => {
    axios
      .get("/api/users/results/")
      .then(({ data }) => setWinners(data))
      .catch(() => setWinners([]));
    axios
      .get("/api/users/entry-rules/")
      .then(({ data }) => setEntryEvents(data))
      .catch(() => setEntryEvents([]));
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  }, [section]);

  return (
    <div className="beach">
      <AwardsDrive driving={driving} passer={passer}>
        <div className="awards-car">
          <div className="awards-body">
          <div className="site-wrap awards-banner">
            <div className="awards-banner-copy">
              <p className="site-kicker">{banner.kicker}</p>
              <h1>{banner.title}</h1>
              <p>{banner.lede}</p>
            </div>
            <nav className="info-tabs" aria-label="Awards and rules">
              {tabs.map((item) => (
                <Link
                  key={item.id}
                  to={item.to}
                  className={section === item.id ? "is-on" : ""}
                  aria-current={section === item.id ? "page" : undefined}
                  onClick={() => beginDrive(item.id)}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
          </div>
          <div className="awards-wheels" aria-hidden="true">
            <span />
            <span />
          </div>
        </div>
      </AwardsDrive>

      {section === "awards" ? (
        <>
          <section className="site-section award-winners">
            <div className="site-wrap">
              <h2>Class winners</h2>
              {winners.length === 0 ? (
                <p>No class winner has been chosen yet.</p>
              ) : (
                <AwardWinnerList
                  winners={winners}
                  filters={filters}
                  across={across}
                  onFilter={(key, value) =>
                    setFilters((current) => settledFilters(winners, { ...current, [key]: value }))
                  }
                  onAcross={setAcross}
                />
              )}
            </div>
          </section>

          <EnterYourCar />
        </>
      ) : null}

      {section === "entry" ? (
        <section className="site-section">
          <div className="site-wrap">
            {entryEvents === null ? null : entryEvents.length === 0 ? (
              <p>The rules for the upcoming shows will be posted here.</p>
            ) : (
              <div className="entry-rule-cards">
                {entryEvents.map((item) => (
                  <article key={item.id} className="company-card company-card-wide">
                    <header className="company-card-head">
                      <p className="site-kicker">{showDate(item.date)}</p>
                      <h2>{item.title}</h2>
                    </header>
                    <div className="company-card-body">
                      {item.rules.length === 0 ? (
                        <p>The rules for this day will be posted here.</p>
                      ) : (
                        <ol className="judge-rules entry-rules">
                          {item.rules.map((rule) => (
                            <li key={rule.id}>
                              <span>{rule.text}</span>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      ) : null}

      {section === "judging" ? (
        <section className="site-section">
          <div className="site-wrap">
            <article className="company-card company-card-wide">
              <header className="company-card-head">
                <p className="site-kicker">Judging rules</p>
                <h2>Preparation and the score</h2>
              </header>
              <div className="company-card-body">
                <div className="judge-layout">
                  <div className="judge-col">
                    <p className="site-kicker">On the field</p>
                    <h3>Competitor’s rules</h3>
                    <ol className="judge-rules">
                      <li>
                        <strong>Schedules</strong>
                        <span>Entries must be in their assigned display positions no later than 8:00 a.m. Missing that time disqualifies the car. No exceptions.</span>
                      </li>
                      <li>
                        <strong>Promotional items</strong>
                        <span>“For Sale” signs, previous trophies, and news clippings are not permitted in the display area.</span>
                      </li>
                      <li>
                        <strong>Cleaning</strong>
                        <span>Last-minute preparation is allowed on the display site until judging begins. Once judging begins on any car, only a light dusting cloth is permitted. There is no dusting while the car is being judged.</span>
                      </li>
                      <li>
                        <strong>Assemblies</strong>
                        <span>Each car is displayed as an assembled unit. Convertible tops, doors, and deck lids must be closed. Bumpers, hubcaps, and the like must be attached in their normal positions, not set alongside the car. Only the Senior Judge may ask for the car to be opened. An entrant is penalized if these items are not as required.</span>
                      </li>
                      <li>
                        <strong>Exhibitor and owner</strong>
                        <span>Must be present during judging.</span>
                      </li>
                    </ol>
                  </div>
                  <div className="judge-col">
                    <p className="site-kicker">On the sheet</p>
                    <h3>How a team scores</h3>
                    <ol className="judge-rules">
                      <li>
                        <strong>The standard</strong>
                        <span>Judging is based on the best preparation, maintenance, and restoration, and on the quality of workmanship and presentation. Every potential point for an item goes to the condition of that item.</span>
                      </li>
                      <li>
                        <strong>The team</strong>
                        <span>Three to five judges make up each team. The number is set the day of the event.</span>
                      </li>
                      <li>
                        <strong>One score</strong>
                        <span>Each judge looks over each category, such as paint, chrome, and interior, and considers its good points. Only one score goes on the sheet for each category, and that score is final.</span>
                      </li>
                      <li>
                        <strong>Places</strong>
                        <span>The first judging produces the class winner, second place, third place, and the rest, plus high-point cars for each specialty category. Best of Show is the class winner with the highest total point score.</span>
                      </li>
                      <li>
                        <strong>Specialty</strong>
                        <span>The top car in each specialty category, such as paint, engine, and interior, with the highest overall points wins that category.</span>
                      </li>
                      <li>
                        <strong>Notes</strong>
                        <span>Judges are encouraged to make notes on their score sheets during the first look, for the best car and the best car in a specific category.</span>
                      </li>
                      <li>
                        <strong>The sheets</strong>
                        <span>Completed judging sheets belong to the Huntington Beach Concours d'Elegance. They are not given to entrants the day of the event.</span>
                      </li>
                    </ol>
                  </div>
                </div>
                <div className="judge-score">
                  <b>100</b>
                  <p>Each car starts with a maximum of 100 points. Points come off for deficiencies, such as missing or unattached items and less than perfect condition. A tie is broken by four Senior Judges, who judge those cars again.</p>
                </div>
                <div className="judge-notes">
                  <article>
                    <h3>Judges</h3>
                    <p>Each car is inspected by a team of qualified judges approved by the Concours committee. Judges, including the Senior Judge, will have had the experience of a judge, a contestant, or the equivalent.</p>
                  </article>
                  <article>
                    <h3>Inspection</h3>
                    <p>The same team judges every car in a division, and finishes each car before starting another class. Lids, caps, and covers open only at the Senior Judge’s request, and only if no tools are required. Each judge judges the entire car.</p>
                  </article>
                  <article>
                    <h3>Re-judging</h3>
                    <p>At the Senior Judge’s discretion, any car or part of a car may be inspected and judged again.</p>
                  </article>
                  <article>
                    <h3>Questions</h3>
                    <p>Questions go to the Senior Judge of the judging team.</p>
                  </article>
                </div>
              </div>
            </article>
          </div>
        </section>
      ) : null}
    </div>
  );
}

export default AwardsScreen;
